"use client"
import React, { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog"
import { Button } from "../../../Components/UI/button"
import { Input } from "../../../Components/UI/input"
import { Label } from "../../../Components/UI/label"
import { Textarea } from "../../../Components/UI/textarea"
import { X, Upload, FileText, Check, Trash2, ArrowLeft, Settings } from "lucide-react"
import { useSelector } from 'react-redux'
import { currentUserHasPermission, getConnectionOptionsWithSearch, getMobileHeaderTitle, getTransactionTypeById } from '../../../Utils/Utilities'
import { DatePicker } from '../../../Components/Common/DatePicker'
import { format } from 'date-fns'
import BusinessInput from '../../../Components/Common/BusinessInput'
import CustomSearchableDropdown from '../../../Components/Common/CustomSearchableDD'
import { manageTransactionAPI } from '../TransactionAPI'
import { AlertBox } from "../../../Components/Common/AlertBox"
import { toast } from 'sonner';
import { getAuthData } from "../../../Utils/AuthUtils"
import apiService from "../../../Services/APIService"
import useIsMobile from "../../../Hooks/useIsMobile"
import { userIsAdmin } from "../../../Store/Reducers"
import SharingOptions from "../../../Components/Common/SharingOptions"
import Policies from "../../Settings/Policies/Policies"

// Reusable Tailwind classes
const txnCommonClasses = {
    spacing: "space-y-1.5",
    input: "h-11 text-sm transition-all duration-200 bg-white sahi-input",
    buttonPrimary: "sahi-primary-button",
    buttonSecondary: "sahi-secondary-button",
    gridCols1: "grid-cols-1",
    gridCols2: "grid-cols-1 sm:grid-cols-2",
    gridCols3: "grid-cols-1 sm:grid-cols-3",
    gap4: "gap-4",
    mb4: "mb-4",
    iconSize: "h-4 w-4",
    iconSizeMd: "h-5 w-5",
    iconSizeLg: "h-6 w-6",
    flexCol: "flex flex-col",
    flexRow: "flex flex-row"
};

const TransactionEntry = ({
    isOpen,
    onClose,
    transactionTypeID = 1,
    connID = null,
    companyName = "",
    prefilledData = {},
    transactionID = null,
    onTransactionSaved = null,
    handleNewTransaction,
    newEntry = false
}) => {
    const isMobile = useIsMobile();
    const xnActions = currentUserHasPermission('XnActions');
    const canDltXns = xnActions === 1;

    // Consolidated state management
    const [formData, setFormData] = useState({
        connection: "",
        contactPerson: "",
        contactPersonDetails: null,
        invoiceNo: "",
        // referenceNo: "",
        totalAmount: "",
        preTaxAmount: "",
        taxAmount: "",
        xnDate: format(new Date(), 'dd-MMM-yy'),
        xnDateAPI: format(new Date(), 'yyyy-MM-dd'),
        // dueDate: "",
        // dueDateAPI: "",
        notes: "",
        shareByEmail: false,
        shareByWhatsapp: false,
        file: null,
        originalFileName: "" // Track original file
    });

    const [uiStates, setUIStates] = useState({
        fileName: "",
        datePickerStates: {},
        deleteAlert: false
    });
    // Redux selectors
    const connections = useSelector(state => state.connectionData.data || []);

    // const filteredConnections = React.useMemo(() => {
    //     if ([1, 3, 5].includes(transactionTypeID)) {
    //         return connections.filter(conn => conn.ConnType?.includes("C"));
    //     }
    //     if ([2, 4, 6].includes(transactionTypeID)) {
    //         return connections.filter(conn => conn.ConnType?.includes("S"));
    //     }
    //     return connections; 
    // }, [connections, transactionTypeID]);

    const transactionType = getTransactionTypeById(transactionTypeID);
    const getDialogTitle = () => {
        const baseTitle = transactionID ? 'Edit ' : '';
        const suffix = transactionID ? '' : ' Entry';
        const businessName = companyName || formData.connection || 'Loading...';

        // Mobile title - no business name
        const mobileTitle = getMobileHeaderTitle(`${baseTitle}${transactionType.LblF}${suffix}`);

        // Desktop title - with business name when available
        const desktopTitle = (transactionID || connID) && (companyName || formData.connection)
            ? `${baseTitle}${transactionType.LblF}${suffix} - ${businessName}`
            : `${baseTitle}${transactionType.LblF}${suffix}`;

        return { mobileTitle, desktopTitle };
    };
    // Process contact data from connection object
    const processContactData = (contact, isPrimary = false) => ({
        CNm: contact.CNm || contact.ContactName,
        UserID: contact.UserID || contact.ContactUserID || contact.ContactID || 0,
        CC: contact.CC || 91,
        Mobile: contact.MNo || contact.Mobile || '',
        Email: contact.Email || contact.Eml || '',
        isPrimary
    });

    // Auto-select contact when connection changes
    const selectDefaultContact = (selectedConnection) => {
        let contacts = selectedConnection.Contacts;
        if (typeof contacts === 'string') {
            try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; }
        }

        // Single contact - select it
        if (contacts && Array.isArray(contacts) && contacts.length === 1) {
            const contactDetails = processContactData(contacts[0]);
            updateFormData({ contactPersonDetails: contactDetails });
            return contacts[0].CNm || '';
        }

        // Multiple contacts - leave empty for user selection
        if (contacts && Array.isArray(contacts) && contacts.length > 1) return '';

        // Default to primary contact
        const primaryContactDetails = processContactData(selectedConnection);
        updateFormData({ contactPersonDetails: primaryContactDetails });
        return selectedConnection.ContactName || '';
    };

    // Add these state variables at the top of your component (where other useState are defined)
    const [emailConfigOpen, setEmailConfigOpen] = useState(false);

    const handleEmailShareChange = (checked) => {
        setFormData(prev => ({
            ...prev,
            shareByEmail: checked
        }));
    };

    const handleSettingsClick = () => {
        setEmailConfigOpen(true);
    };

    // Unified form data updater
    const updateFormData = (updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };


    // Handle Tax amount change
    const handleTaxAmountChange = (fieldName, e) => {
        const value = e.target.value;
        if (value === '') {
            updateFormData({ [fieldName]: '' });
            return;
        }

        const numericValue = value.replace(/[^0-9.]/g, '');
        const parts = numericValue.split('.');
        if (parts.length > 2 || (parts[1] && parts[1].length > 2)) return;

        const numberValue = parseFloat(numericValue);
        if (!isNaN(numberValue) && numberValue > 999999999.99) return;

        updateFormData({ [fieldName]: numericValue });
    };

    // Function which tells tax field is needed or not
    const needsTaxFields = () => {
        return [1, 2, 3, 4].includes(transactionTypeID);
    };

    // Function which will calculate tax total
    const calculateTaxAmount = () => {
        if (!needsTaxFields()) return parseFloat(formData.totalAmount) || 0;

        const preTaxAmt = parseFloat(formData.preTaxAmount) || 0;
        const totalAmt = parseFloat(formData.totalAmount) || 0;
        return totalAmt - preTaxAmt;
    };

    // Handle date changes with business logic validation
    const handleDateChange = (fieldID, date) => {
        if (date) {
            const formattedDate = format(date, 'dd-MMM-yy');
            const apiDate = format(date, 'yyyy-MM-dd');

            // Clear due date if invoice date is changed and due date becomes invalid
            // if (fieldID === 'xnDate' && formData.dueDate) {
            //     const selectedInvoiceDate = new Date(date);
            //     const currentDueDate = new Date(formData.dueDate);
            //     selectedInvoiceDate.setHours(0, 0, 0, 0);
            //     currentDueDate.setHours(0, 0, 0, 0);

            //     if (currentDueDate < selectedInvoiceDate) {
            //         updateFormData({
            //             [fieldID]: formattedDate,
            //             [`${fieldID}API`]: apiDate,
            //             dueDate: '',
            //             dueDateAPI: ''
            //         });

            //         toast.info("Due date cleared", {
            //             description: "Due date was cleared because it was before the new invoice date."
            //         });
            //         return;
            //     }
            // }

            updateFormData({
                [fieldID]: formattedDate,
                [`${fieldID}API`]: apiDate
            });
        } else {
            updateFormData({
                [fieldID]: '',
                [`${fieldID}API`]: ''
            });
        }

        setUIStates(prev => ({
            ...prev,
            datePickerStates: { ...prev.datePickerStates, [fieldID]: false }
        }));
    };

    // Handle dropdown selections with contact details processing
    const handleSelectionChange = (fieldName, value, selectedConnection = null) => {
        if (fieldName === 'connection' && connID) return;

        if (fieldName === 'connection') {
            updateFormData({
                [fieldName]: value,
                contactPerson: selectedConnection ? selectDefaultContact(selectedConnection) : ''
            });
        } else if (fieldName === 'contactPerson') {
            const connectionToUse = connID
                ? connections.find(conn => conn.ConnID === connID)
                : connections.find(conn => conn.ConnID === formData.connection);

            if (connectionToUse) {
                const contactDetails = findContactInConnection(connectionToUse, value);
                updateFormData({
                    [fieldName]: value,
                    contactPersonDetails: contactDetails
                });
            } else {
                updateFormData({ [fieldName]: value });
            }
        } else {
            updateFormData({ [fieldName]: value });
        }
    };

    // Find specific contact in connection data
    const findContactInConnection = (connection, contactName) => {
        // Check primary contact
        if (connection.ContactName === contactName) {
            return processContactData(connection, true);
        }

        // Check additional contacts
        let contacts = connection.Contacts;
        if (typeof contacts === 'string') {
            try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; }
        }

        if (contacts && Array.isArray(contacts)) {
            const foundContact = contacts.find(contact => contact.CNm === contactName);
            return foundContact ? processContactData(foundContact) : null;
        }

        return null;
    };

    // Handle file upload with validation
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type === "application/pdf") {
                // Validate file size (max 1MB)
                if (file.size > 1 * 1024 * 1024) {
                    toast.error("File size too large");
                    event.target.value = "";
                    return;
                }
                updateFormData({ file });
                setUIStates(prev => ({ ...prev, fileName: file.name }));
            } else {
                toast.error("Invalid file format", {
                    description: "Only PDF files are allowed"
                });
                event.target.value = "";
            }
        }
    };

    // Validate dates according to business rules
    const validateDates = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (formData.xnDate) {
            const invoiceDate = new Date(formData.xnDate);
            invoiceDate.setHours(0, 0, 0, 0);
            if (invoiceDate > today) {
                toast.error("Please select valid invoice date.", {
                    description: "Invoice date cannot be in the future."
                });
                return false;
            }
        }

        // if (formData.dueDate) {
        //     const dueDate = new Date(formData.dueDate);
        //     dueDate.setHours(0, 0, 0, 0);

        //     if (formData.xnDate) {
        //         const invoiceDate = new Date(formData.xnDate);
        //         invoiceDate.setHours(0, 0, 0, 0);
        //         if (dueDate < invoiceDate) {
        //             toast.error("Please select valid due date.", {
        //                 description: "Due date must be on or after the invoice date."
        //             });
        //             return false;
        //         }
        //     } else if (dueDate < today) {
        //         toast.error("Please select valid due date.", {
        //             description: "Due date cannot be in the past."
        //         });
        //         return false;
        //     }
        // }

        return true;
    };

    // Handle form submission with comprehensive validation
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.connection && !connID) {
            toast.error("Please select a contact.");
            return;
        } else if (!formData.invoiceNo) {
            toast.error("Please enter an invoice no.");
            return;
        } else if (needsTaxFields()) {
            if (!formData.preTaxAmount) {
                toast.error("Please enter Pre-Tax Amount.");
                return;
            }
            if (!formData.totalAmount) {
                toast.error("Please enter Total Amount.");
                return;
            }
            if (parseFloat(formData.totalAmount) < parseFloat(formData.preTaxAmount)) {
                toast.error("Total Amount must be greater than Pre-Tax Amount.");
                return;
            }
        } else if (!formData.totalAmount) {
            toast.error("Please enter Amount.");
            return;
        } else if (!formData.xnDateAPI) {
            toast.error("Please select a valid date.");
            return;
        }

        if (!validateDates()) return;

        try {
            const contactDetails = formData.contactPersonDetails || {};

            let uploadedFileName = '';

            // Handle PDF upload if file is selected
            if (formData.file) {
                try {
                    const uploadResult = await apiService.uploadFile(
                        formData.file,
                        "Transactions",
                        "pdf"
                    );

                    if (uploadResult.error) {
                        toast.error(uploadResult.message);
                        return;
                    }
                    uploadedFileName = uploadResult.data.relativePath;
                } catch (uploadError) {
                    console.error('PDF upload error:', uploadError);
                    toast.error('Failed to upload PDF file');
                    return;
                }
            }

            // Determine final file name - use new upload, existing file, or empty
            let finalFileName = '';
            if (uploadedFileName) {
                // New file was uploaded
                finalFileName = uploadedFileName;
            } else if (formData.originalFileName && !formData.file) {
                // Keep existing file (no new file selected and original exists)
                finalFileName = prefilledData.fileName || formData.originalFileName;
            }
            // If formData.file is null and originalFileName is empty, finalFileName remains empty (file removed)

            const transactionData = {
                xnID: transactionID || 0,
                connID: connID || formData.connection,
                xnNo: formData.invoiceNo || "",
                xnDate: formData.xnDateAPI || null,
                xnTypeID: transactionTypeID,
                xnFile: finalFileName,
                connContactName: formData.contactPerson || "",
                connContactID: contactDetails.UserID || 0,
                connCC: contactDetails.CC || 91,
                connMobile: contactDetails.Mobile || '',
                connEmail: contactDetails.Email || '',
                currencyID: 1,
                amtSubTotal: needsTaxFields() ? (parseFloat(formData.preTaxAmount) || 0) : (parseFloat(formData.totalAmount) || 0),
                amtTaxTotal: needsTaxFields() ? calculateTaxAmount() : 0,
                amtGrandTotal: (parseFloat(formData.totalAmount) || 0),
                notes: formData.notes || "",
                shareByEmail: formData.shareByEmail || false,
                shareByWhatsapp: formData.shareByWhatsapp || false
            };

            const operation = transactionID ? 'EDIT' : 'ADD';
            await manageTransactionAPI(transactionData, operation);

            toast.success(`Transaction ${transactionID ? 'updated' : 'saved'}.`);

            if (onTransactionSaved) onTransactionSaved();
            resetForm(); // Reset form only on success
            onClose();
        } catch (error) {
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : `Failed to ${transactionID ? 'update' : 'save'} ${transactionType.LblF}.`;

            toast.error(errorMessage);
        }
    };

    // Handle transaction deletion
    // Handle transaction deletion
    const handleDeleteTransaction = async () => {
        try {
            const contactDetails = formData.contactPersonDetails || {};

            const deleteData = {
                xnID: transactionID,
                connID: connID || formData.connection,
                xnNo: formData.invoiceNo || "",
                xnDate: formData.xnDateAPI || null,
                xnTypeID: transactionTypeID,
                xnFile: prefilledData.fileName || '',
                connContactName: formData.contactPerson || "",
                connContactID: contactDetails.UserID || 0,
                connCC: contactDetails.CC || 91,
                connMobile: contactDetails.Mobile || '',
                connEmail: contactDetails.Email || '',
                currencyID: 1,
                amtSubTotal: needsTaxFields() ? (parseFloat(formData.preTaxAmount) || 0) : (parseFloat(formData.totalAmount) || 0),
                amtTaxTotal: needsTaxFields() ? calculateTaxAmount() : 0,
                amtGrandTotal: (parseFloat(formData.totalAmount) || 0),
                notes: formData.notes || "",
                shareByEmail: false,
                shareByWhatsapp: false
            };

            await manageTransactionAPI(deleteData, 'DELETE');
            toast.success('Transaction deleted.');

            if (onTransactionSaved) onTransactionSaved();
            onClose();
        } catch (error) {
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : 'Failed to delete transaction.';
            toast.error(errorMessage);
        }
    };

    // Reset form to initial state
    const resetForm = () => {
        const today = new Date();
        setFormData({
            connection: connID || "",
            contactPerson: "",
            contactPersonDetails: null,
            invoiceNo: "",
            // referenceNo: "",
            totalAmount: "",
            preTaxAmount: "",
            taxAmount: "",
            xnDate: format(today, 'dd-MMM-yy'),
            xnDateAPI: format(today, 'yyyy-MM-dd'),
            // dueDate: "",
            // dueDateAPI: "",
            notes: "",
            shareByEmail: false,
            shareByWhatsapp: false,
            file: null,
            originalFileName: ""
        });
        setUIStates({
            fileName: "",
            datePickerStates: {},
            deleteAlert: false
        });
    };

    // Enhanced close handler
    const handleClose = () => {
        resetForm();
        onClose();
    };
    // Function to render disabled connection field in mobile
    const renderDisableConn = () => {
        const isShow = isMobile && connID && companyName;

        return isShow ? (
            <div key='connID' className="space-y-1.5">
                <Label htmlFor='connID' required={true}>
                    Contact
                </Label>
                <Input
                    id='connID'
                    name='connID'
                    value={companyName}
                    onChange={() => { }} // No-op since it's disabled
                    placeholder={'Contact'}
                    className="h-11 text-sm transition-all duration-200 bg-white sahi-input"
                    type={'text'}
                    autoComplete={'off'}
                    maxLength={50}
                    disabled={true}
                />
            </div>
        ) : null;
    };

    const renderConnectionField = () => {
        if (connID) return null;

        const filteredConnections = connections.filter(conn => !(conn.IsCold === 1 || conn.IsCold === true));
        const connectionOptions = getConnectionOptionsWithSearch(filteredConnections);


        return (
            <div className={txnCommonClasses.spacing}>
                <Label htmlFor="connection" required={true}>Contact</Label>
                <CustomSearchableDropdown
                    options={connectionOptions}
                    value={formData.connection}
                    onSelect={(value, option) => handleSelectionChange('connection', value, option?.connection)}
                    placeholder="e.g. John Infrastructure"
                    searchPlaceholder="Search by name, city, or person."
                    displayKey="label"
                    valueKey="value"
                    allowAdd={false}
                    label="Contact"
                    emptyMessage="No contacts found."
                    enableMultiFieldSearch={true}
                />
            </div>
        );
    };

    // Render contact person field with smart options
    const renderContactPersonField = () => {

        const connectionToUse = connID
            ? connections.find(conn => conn.ConnID === connID)
            : connections.find(conn => conn.ConnID === formData.connection);


        if (!connectionToUse && !connID) {
            return (
                <div className={txnCommonClasses.spacing}>
                    <Label htmlFor="contactPerson" required={true}>Contact Person</Label>
                    <CustomSearchableDropdown
                        options={[]}
                        value=""
                        onSelect={() => { }}
                        placeholder="Select contact first"
                        disabled={true}
                        label={`Contact`}
                    />
                </div>
            );
        }

        if (!connectionToUse) {
            return (
                <div className={txnCommonClasses.spacing}>
                    <Label htmlFor="contactPerson" required={true}>Contact Person</Label>
                    <Input
                        id="contactPerson"
                        value={formData.contactPerson}
                        onChange={(e) => updateFormData({ contactPerson: e.target.value })}
                        placeholder="Enter contact person"
                        className={txnCommonClasses.input}
                    />
                </div>
            );
        }

        // Build contact options
        let contacts = connectionToUse.Contacts;
        if (typeof contacts === 'string') {
            try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; }
        }

        let contactOptions = [];

        if (connectionToUse.ContactName) {
            contactOptions.push({
                value: connectionToUse.ContactName,
                label: connectionToUse.ContactName
            });
        }

        if (contacts && Array.isArray(contacts) && contacts.length > 0) {
            const additionalContacts = contacts.map(contact => ({
                value: contact.CNm,
                label: contact.CNm
            }));

            const allContacts = [...contactOptions, ...additionalContacts];
            contactOptions = allContacts.filter((contact, index, self) =>
                index === self.findIndex(c => c.value === contact.value)
            );
        }

        contactOptions.sort((a, b) => a.label.localeCompare(b.label));

        // Hide the field if there's only one option
        if (contactOptions.length === 1) {
            return null;
        }
        return (
            <div className={txnCommonClasses.spacing}>
                <Label htmlFor="contactPerson" required={true}>Contact Person</Label>
                <CustomSearchableDropdown
                    options={contactOptions}
                    value={formData.contactPerson}
                    onSelect={(value) => handleSelectionChange('contactPerson', value)}
                    placeholder="Select contact person"
                    searchPlaceholder="Search contacts..."
                    displayKey="label"
                    valueKey="value"
                    allowAdd={false}
                    label={`Contact`}
                    emptyMessage="No contacts found"
                />
            </div>
        );
    };

    // Render date field with business rules
    const renderDateField = (fieldID, label, placeholder, required = false) => {
        const dateValue = formData[fieldID] ? new Date(formData[fieldID]) : undefined;
        const isDatePickerOpen = uiStates.datePickerStates[fieldID] || false;
        const today = new Date();
        const currentYear = today.getFullYear();
        today.setHours(0, 0, 0, 0);

        let minDate, maxDate, restrictPastDates, fromYear, toYear, defaultMonth;

        if (fieldID === 'xnDate') {
            minDate = null;
            maxDate = today;
            restrictPastDates = (date) => {
                const dateOnly = new Date(date);
                dateOnly.setHours(0, 0, 0, 0);
                return dateOnly > today;
            };
            fromYear = currentYear - 10;
            toYear = currentYear;
            defaultMonth = dateValue || today;
        } else if (fieldID === 'dueDate') {
            const invoiceDate = formData.xnDate ? new Date(formData.xnDate) : null;

            if (invoiceDate) {
                invoiceDate.setHours(0, 0, 0, 0);
                minDate = invoiceDate;
            } else {
                minDate = today;
            }

            maxDate = null;
            restrictPastDates = (date) => {
                const dateOnly = new Date(date);
                dateOnly.setHours(0, 0, 0, 0);

                if (invoiceDate) {
                    return dateOnly < invoiceDate;
                } else {
                    return dateOnly < today;
                }
            };
            fromYear = currentYear;
            toYear = currentYear + 10;
            defaultMonth = dateValue || (invoiceDate || today);
        }

        return (
            <div className="w-1/2 lg:w-full">
                <DatePicker
                    key={fieldID}
                    fieldID={fieldID}
                    value={dateValue}
                    onChange={(date) => handleDateChange(fieldID, date)}
                    placeholder={placeholder}
                    label={label}
                    required={required}
                    className={txnCommonClasses.spacing}
                    disabled={restrictPastDates}
                    minDate={minDate}
                    maxDate={maxDate}
                    fromYear={fromYear}
                    toYear={toYear}
                    defaultMonth={defaultMonth}
                    onOpenChange={(open) => setUIStates(prev => ({
                        ...prev,
                        datePickerStates: { ...prev.datePickerStates, [fieldID]: open }
                    }))}
                    open={isDatePickerOpen}
                    allowDeselect={false}
                    showYearNavigation={true}
                />
            </div>
        );
    };

    // Initialize form with prefilled data
    useEffect(() => {
        if (prefilledData && Object.keys(prefilledData).length > 0) {
            const today = new Date();
            const updatedData = {
                xnDate: format(today, 'dd-MMM-yy'),
                xnDateAPI: format(today, 'yyyy-MM-dd'),
                ...prefilledData
            };
            if (prefilledData.xnDate && !prefilledData.xnDateAPI) {
                try {
                    const invoiceDate = new Date(prefilledData.xnDate);
                    if (!isNaN(invoiceDate.getTime())) {
                        updatedData.xnDateAPI = format(invoiceDate, 'yyyy-MM-dd');
                    }
                } catch (e) {
                    console.error('Error parsing invoice date:', e);
                }
            }

            // Set original filename for edit mode
            if (prefilledData.fileName) {
                updatedData.originalFileName = prefilledData.fileName;
                // Extract just the filename for display
                const displayFileName = prefilledData.fileName.includes('/')
                    ? prefilledData.fileName.split('/').pop()
                    : prefilledData.fileName;
                setUIStates(prev => ({ ...prev, fileName: displayFileName }));
            }

            // Process contact details from prefilled data
            if (prefilledData.mobile || prefilledData.email || prefilledData.cc) {
                updatedData.contactPersonDetails = {
                    CNm: prefilledData.contactName || prefilledData.contactPerson,
                    UserID: 0, // Default value since we don't have this from the transaction
                    CC: prefilledData.cc || 91,
                    Mobile: prefilledData.mobile || '',
                    Email: prefilledData.email || '',
                    isPrimary: false
                };
            }

            setFormData(prev => ({ ...prev, ...updatedData }));
        }
    }, [prefilledData]);


    // Auto-set contact person when connID is provided
    useEffect(() => {
        if (connID && connections.length > 0 && isOpen) {
            const connectionToUse = connections.find(
                conn => conn.ConnID === connID
            );
            if (connectionToUse && !formData.contactPerson) {
                const defaultContact = selectDefaultContact(connectionToUse);
                if (defaultContact) {
                    setFormData(prev => {
                        return { ...prev, contactPerson: defaultContact };
                    });
                }
            }
        }
    }, [connID, connections, formData.contactPerson, isOpen]);

    // Reset form in ADD mode
    useEffect(() => {
        if (newEntry && !transactionID && isOpen) {
            setFormData({
                connection: connID || "",
                contactPerson: "",
                contactPersonDetails: null,
                invoiceNo: "",
                totalAmount: "",
                preTaxAmount: "",
                taxAmount: "",
                xnDate: format(new Date(), "dd-MMM-yy"),
                xnDateAPI: format(new Date(), "yyyy-MM-dd"),
                notes: "",
                shareByEmail: false,
                shareByWhatsapp: false,
                file: null,
                originalFileName: ""
            });
            setUIStates({
                fileName: "",
                datePickerStates: {},
                deleteAlert: false
            });
        }
    }, [newEntry, connID, transactionID, isOpen]);

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent className={`flex flex-col w-full h-full sm:h-auto sm:rounded-lg rounded-none ${needsTaxFields() ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}>
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className={txnCommonClasses.iconSizeLg} />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium">
                                    <div className="truncate">
                                        {getDialogTitle().mobileTitle}
                                    </div>
                                </DialogTitle>
                            </div>
                            {/* Right Side */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {(transactionID && canDltXns) && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 p-0 hover:bg-white/10 hover:text-white"
                                        onClick={() => setUIStates(prev => ({ ...prev, deleteAlert: true }))}
                                    >
                                        <Trash2 className={txnCommonClasses.iconSize} />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <DialogTitle className="hidden sm:block text-lg md:text-xl">
                            {getDialogTitle().desktopTitle}
                        </DialogTitle>
                        <div className="hidden sm:flex items-center gap-2">
                            {(transactionID && canDltXns) && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 p-0 hover:bg-white/10 hover:text-white"
                                    onClick={() => setUIStates(prev => ({ ...prev, deleteAlert: true }))}
                                >
                                    <Trash2 className={txnCommonClasses.iconSize} />
                                </Button>
                            )}
                            <DialogClose className="flex-shrink-0" tabIndex={-1}>
                                <X className={txnCommonClasses.iconSizeMd} />
                                <span className="sr-only">Close</span>
                            </DialogClose>
                        </div>
                    </DialogHeader>

                    <div className="px-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0">
                        <div className="space-y-4 pb-6">



                            {(() => {
                                const connectionField = renderConnectionField();
                                const contactPersonField = renderContactPersonField();
                                const disabledConnField = renderDisableConn();

                                // If we have a disabled connection field (mobile with connID), handle it separately
                                if (disabledConnField) {
                                    return (
                                        <div className={txnCommonClasses.mb4}>
                                            {disabledConnField}
                                            {contactPersonField && (
                                                <div className={`${txnCommonClasses.spacing} ${isMobile ? 'mt-4' : ''}`}>
                                                    {contactPersonField}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                // If we have both connection and contact person fields
                                if (connectionField && contactPersonField) {
                                    return (
                                        <div className={`grid ${txnCommonClasses.gridCols2} ${txnCommonClasses.gap4} ${txnCommonClasses.mb4}`}>
                                            {connectionField}
                                            {contactPersonField}
                                        </div>
                                    );
                                }

                                // If only connection field exists
                                if (connectionField && !contactPersonField) {
                                    return (
                                        <div className={`grid ${txnCommonClasses.gridCols2} ${txnCommonClasses.gap4} ${txnCommonClasses.mb4}`}>
                                            <div className="col-span-1">
                                                {connectionField}
                                            </div>
                                            <div className="hidden sm:block"></div> {/* Empty placeholder for desktop grid alignment */}
                                        </div>
                                    );
                                }

                                // If only contact person field exists - show it in half width on the left
                                if (!connectionField && contactPersonField) {
                                    return (
                                        <div className={`grid ${txnCommonClasses.gridCols2} ${txnCommonClasses.gap4} ${txnCommonClasses.mb4}`}>
                                            <div className="col-span-1">
                                                {contactPersonField}
                                            </div>
                                            <div className="hidden sm:block"></div> {/* Empty placeholder for desktop grid alignment */}
                                        </div>
                                    );
                                }

                                // If neither field exists, don't render anything (no extra gap)
                                return null;
                            })()}

                            {/* Invoice Details Row */}
                            <div className={`grid ${needsTaxFields() ? txnCommonClasses.gridCols3 : txnCommonClasses.gridCols2} ${txnCommonClasses.gap4}`}>
                                <div className={txnCommonClasses.spacing}>
                                    <Label htmlFor="invoiceNo" required={true}>
                                        {transactionType?.LblF} No.
                                    </Label>
                                    <Input
                                        id="invoiceNo"
                                        value={formData.invoiceNo}
                                        onChange={(e) => updateFormData({ invoiceNo: e.target.value })}
                                        placeholder="e.g. INV-001"
                                        className={txnCommonClasses.input}
                                    />
                                </div>

                                {needsTaxFields() ? (
                                    <>
                                        <BusinessInput
                                            fieldID="preTaxAmount"
                                            label="Pre-Tax Amount"
                                            placeholder="e.g. 5,000"
                                            value={formData.preTaxAmount}
                                            onChange={(e) => handleTaxAmountChange("preTaxAmount", e)}
                                            required={true}
                                            maxDecimalPlaces={2}
                                            maxIntegerDigits={9}
                                            className={txnCommonClasses.spacing}
                                            disabled={false}
                                        />
                                        <BusinessInput
                                            fieldID="totalAmount"
                                            label="Total Amount"
                                            placeholder="e.g. 5,500"
                                            value={formData.totalAmount}
                                            onChange={(e) => handleTaxAmountChange("totalAmount", e)}
                                            required={true}
                                            maxDecimalPlaces={2}
                                            maxIntegerDigits={9}
                                            className={txnCommonClasses.spacing}
                                            disabled={false}
                                        />
                                    </>
                                ) : (
                                    <BusinessInput
                                        fieldID="totalAmount"
                                        label="Amount"
                                        placeholder="e.g. 25,000"
                                        value={formData.totalAmount}
                                        onChange={(e) => handleTaxAmountChange("totalAmount", e)}
                                        required={true}
                                        maxDecimalPlaces={2}
                                        maxIntegerDigits={9}
                                        className={`${txnCommonClasses.spacing} col-span-1 sm:col-span-1`}
                                        disabled={false}
                                    />
                                )}
                            </div>

                            {/* Dates and File Row */}
                            <div className={`grid ${needsTaxFields() ? txnCommonClasses.gridCols3 : txnCommonClasses.gridCols2} ${txnCommonClasses.gap4}`}>
                                {renderDateField("xnDate", `${transactionType?.LblF} Date`, "Select Date", true)}



                                <div className={txnCommonClasses.spacing}>
                                    <Label htmlFor="file" className="text-sm font-medium">
                                        File (PDF only)
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="file"
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full justify-start text-left h-11 px-3 bg-white border-sahi-border-color text-sm transition-all duration-200"
                                            onClick={() => document.getElementById("file").click()}
                                        >
                                            {uiStates.fileName ? (
                                                <div className="flex items-center gap-2 truncate w-full">
                                                    <FileText className={`${txnCommonClasses.iconSize} text-sahi-blue flex-shrink-0`} />
                                                    <span className="truncate text-sm flex-1">{uiStates.fileName}</span>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-4 w-4 p-0 flex-shrink-0 hover:bg-gray-100 rounded-full"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            updateFormData({ file: null, originalFileName: "" });
                                                            setUIStates(prev => ({ ...prev, fileName: "" }));
                                                            document.getElementById("file").value = "";
                                                        }}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-sub-text-color">
                                                    <Upload className={txnCommonClasses.iconSize} />
                                                    <span className="text-sm">Choose PDF File</span>
                                                </div>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Notes Section */}
                            <div className={txnCommonClasses.spacing}>
                                <Label htmlFor="notes">Notes</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => updateFormData({ notes: e.target.value })}
                                    placeholder="e.g. Payment for laptop purchase"
                                    className="text-sm transition-all duration-200 bg-white sahi-input resize-none"
                                    rows={2}
                                />
                            </div>

                            {/* Email Sharing Section - Only show in ADD mode */}
                            {false && <SharingOptions
                                connID={formData.connection || connID}
                                isEmailShared={formData.shareByEmail}
                                onEmailShareChange={handleEmailShareChange}
                                showInAddMode={true}
                                isEditMode={!!transactionID}
                                onSettingsClick={handleSettingsClick}
                                className={txnCommonClasses.spacing}
                            />}
                        </div>

                        {/* Action Buttons */}
                        <div className=" sm:px-0 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3 border-t sm:border-t-0">
                            <div className="flex flex-col gap-4 sm:flex-row sm:justify-start sm:gap-3 w-full">

                                {/* Mobile Layout - Save full width only */}
                                <div className="flex sm:hidden w-full">
                                    <Button onClick={handleSubmit} className="sahi-primary-button w-full">
                                        <Check className="h-4 w-4" />
                                        {transactionID ? 'Update' : 'Save'} {transactionType.LblF}
                                    </Button>
                                </div>

                                {/* Desktop Layout - Save + Close */}
                                <div className="hidden sm:flex sm:gap-3">
                                    <Button onClick={handleSubmit} className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]">
                                        <Check className="h-4 w-4" />
                                        {transactionID ? 'Update' : 'Save'} {transactionType.LblF}
                                    </Button>
                                    <Button variant="outline" tabIndex={-1} onClick={handleClose} className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]">
                                        <X className="h-5 w-5" />
                                        <span>Close</span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertBox
                open={uiStates.deleteAlert}
                onOpenChange={(open) => setUIStates(prev => ({ ...prev, deleteAlert: open }))}
                title="Please Confirm"
                description="Are you sure you wish to delete this transaction?"
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className={txnCommonClasses.iconSize} />
                    },
                    {
                        label: "Delete Transaction",
                        variant: "action",
                        value: "delete",
                        className: "w-full sm:w-auto",
                        icon: <Trash2 className={txnCommonClasses.iconSize} />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "delete") {
                        handleDeleteTransaction();
                    }
                }}
            />

            {/* Email configure dialog box */}
            <Policies open={emailConfigOpen} onOpenChange={setEmailConfigOpen} />
        </>
    );
};

export default TransactionEntry;